import {createSlice} from '@reduxjs/toolkit';
import {JwtToken} from './types';

type SliceState = {
  token: JwtToken | null;
  email: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  /**
   * False for accounts made with Google, Apple or Facebook sign-in that never
   * set a password (backend#263); null until /me says (older backend too), in
   * which case the apps keep their usual forms.
   */
  hasUsablePassword: boolean | null;
  /**
   * `reauth_methods` from /me (backend#263 Phase B): how the user can prove
   * it is them before an email change. Null until /me says, or when an older
   * backend omits it (the apps then keep the Phase A behaviour).
   */
  reauthMethods: string[] | null;
  debugEmail: string;
  debugPremiumOverride: boolean | null;
  debugLanguageOverride: string | null;
  debugApiUrl: string | null;
};

const initialState: SliceState = {
  token: null,
  email: '',
  firstName: '',
  lastName: '',
  isAdmin: false,
  hasUsablePassword: null,
  reauthMethods: null,
  debugEmail: '',
  debugPremiumOverride: null,
  debugLanguageOverride: null,
  debugApiUrl: null,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    storeToken: (state, {payload: token}: {payload: JwtToken}) => {
      state.token = token;
    },
    storeUserData: (
      state,
      {
        payload: {
          token,
          userInfo: {
            email,
            firstName,
            lastName,
            isAdmin,
            hasUsablePassword,
            reauthMethods,
          },
        },
      }: {
        payload: {
          token?: JwtToken;
          userInfo: {
            email: string;
            firstName: string;
            lastName: string;
            isAdmin: boolean;
            hasUsablePassword?: boolean | null;
            reauthMethods?: string[] | null;
          };
        };
      },
    ) => {
      if (token) {
        state.token = token;
      }
      state.email = email;
      state.firstName = firstName;
      state.lastName = lastName;
      state.isAdmin = isAdmin;
      state.hasUsablePassword = hasUsablePassword ?? null;
      state.reauthMethods = reauthMethods ?? null;
    },
    setDebugEmail: (state, {payload: email}: {payload: string}) => {
      state.debugEmail = email;
      if (!email) {
        state.debugPremiumOverride = null;
        state.debugLanguageOverride = null;
      }
    },
    setDebugPremiumOverride: (state, {payload}: {payload: boolean | null}) => {
      state.debugPremiumOverride = payload;
    },
    setDebugLanguageOverride: (state, {payload}: {payload: string | null}) => {
      state.debugLanguageOverride = payload;
    },
    setDebugApiUrl: (state, {payload}: {payload: string | null}) => {
      state.debugApiUrl = payload;
    },
  },
});

export const {
  storeToken,
  storeUserData,
  setDebugEmail,
  setDebugPremiumOverride,
  setDebugLanguageOverride,
  setDebugApiUrl,
} = userSlice.actions;

export default userSlice.reducer;
